import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Grid, styled, Divider } from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

// NEW ICONS
// Light Theme
import supportManagementIconLight from '../assets/images/light/support_management.png';
import monitoringIconLight from '../assets/images/light/monitoring.png';
import serviceConfigurationIconLight from '../assets/images/light/service_configuration.png';
import securityIconLight from '../assets/images/light/security.png';
import cloudIconLight from '../assets/images/light/Cloud.png';
import cicdIconLight from '../assets/images/light/cicd.png';
import qaTestingIconLight from '../assets/images/light/QA_dashboard.png';
import platformDeepDiveIconLight from '../assets/images/light/platform_deep_dive.png';
import userConfigIconLight from '../assets/images/light/user-config.png';

// Dark Theme
import supportManagementIconDark from '../assets/images/dark/support_management-dark.png';
import monitoringIconDark from '../assets/images/dark/monitoring-dark.png';
import serviceConfigurationIconDark from '../assets/images/dark/service_configuration-dark.png';
import securityIconDark from '../assets/images/dark/security-dark.png';
import cloudIconDark from '../assets/images/dark/Cloud-dark.png';
import cicdIconDark from '../assets/images/dark/cicd-dark.png';
import qaTestingIconDark from '../assets/images/dark/QA_dashboard-dark.png';
import platformDeepDiveIconDark from '../assets/images/dark/platform_deep_dive-dark.png';
import userConfigIconDark from '../assets/images/dark/user-config-dark.png';


// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
  padding: '24px 190px',
  backgroundColor: theme.palette.background.default,
  minHeight: 'calc(100vh - 64px)',
}));



const WelcomeTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 'bold',
  fontFamily: '"Proxima Nova"',
  color: theme.palette.text.primary,
  marginBottom: '4px',
}));

const WelcomeSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.mode === 'dark' ? '#F3F3F3' : '#2E2E2E', // BK2 for dark theme, BK9 for light theme
  '& strong': {
    color: '#1674B4',
  },
}));

const FeatureCard = styled(Paper)(({ theme, special }) => ({
  backgroundColor: special 
    ? (theme.palette.mode === 'dark' ? '#1674B4' : '#E3F2FD') 
    : theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '24px',
  height: '124px',
  width: '100%',
  maxWidth: special ? '982px' : '470px',
  margin: special ? '0 auto' : undefined,
  borderRadius: '8px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.border.main}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const IconImage = styled('img')(({ special }) => ({
  width: special ? '60px' : '80px',
  height: special ? '60px' : '80px',
  marginRight: '24px',
}));

const CardContent = styled(Box)({
  textAlign: 'left',
});

const Subtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  color: theme.palette.mode === 'dark' ? '#D9D9D9' : '#1674B4',
  marginBottom: '4px',
  letterSpacing: '0.01em',
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: '8px',
  fontWeight: 400,
  lineHeight: '1.5',
}));

const ReadMoreLink = styled('a')(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
  fontWeight: 'bold',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  }
}));

const featureSectionMapping = {
  'Support Management': 'support_management',
  'Monitoring': 'monitoring',
  'Service configuration': 'service_configuration',
  'Security': 'security',
  'Cloud': 'cloud_management',
  'CI/CD Flows': 'cicd_flows',
  'QA & Testing': 'qa_testing',
};

// Scroll to top utility
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

function DevOpsDashboard({ isAdmin, darkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState('');
  
  // Scroll to top when location changes
  useEffect(() => {
    scrollToTop();
  }, [location]);
  
  const [allowedSections, setAllowedSections] = useState([]);
  const [userName, setUserName] = useState('');
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [lastLogin, setLastLogin] = useState('');

  // Fetch the count of open tickets for the current user
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 5000; // 5 seconds

    const fetchOpenTicketsCount = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${API_URL}/tickets/user/count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
        
        if (isMounted) {
          setOpenTicketsCount(response.data.openTicketsCount || 0);
          // Reset retry count on successful fetch
          retryCount = 0;
        }
      } catch (error) {
        if (isMounted) {
          if (retryCount < maxRetries) {
            retryCount++;
            console.log(`Retry ${retryCount}/${maxRetries} - Error fetching open tickets count:`, error.message);
            // Retry after delay
            setTimeout(fetchOpenTicketsCount, retryDelay);
          } else {
            console.error('Max retries reached. Failed to fetch open tickets count:', error.message);
            setOpenTicketsCount(0);
          }
        }
      }
    };
    
    // Initial fetch
    fetchOpenTicketsCount();
    
    // Set up polling with longer interval (5 minutes instead of 30 seconds)
    const intervalId = setInterval(fetchOpenTicketsCount, 300000);
    
    // Cleanup
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const email = localStorage.getItem('user_email');
    const token = localStorage.getItem('auth_token');
    const lastLoginTimestamp = localStorage.getItem('last_login');

    if (email && token) {
      setUserEmail(email);
      if (email.includes('@')) {
        const name = email.split('@')[0];
        setUserName(name.charAt(0).toUpperCase() + name.slice(1));
      }
      if (lastLoginTimestamp) {
        setLastLogin(formatTimeAgo(lastLoginTimestamp));
      }
      
      const fetchAllowedSections = async () => {
        try {
          const API_URL = process.env.REACT_APP_API_URL;
          const response = await axios.get(`${API_URL}/api/user-access/my-sections`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAllowedSections(response.data || []);
        } catch (error) {
          console.error('Error fetching allowed sections:', error.response ? error.response.data : error.message);
          setAllowedSections([]); 
        }
      };
      fetchAllowedSections();
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 0 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 18) {
      return 'Good Afternoon';
    } else {
      return 'Good Evening';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const loginTime = new Date(timestamp);
    const seconds = Math.floor((now - loginTime) / 1000);

    if (seconds < 60) return 'just now';

    let interval = seconds / 31536000;
    if (interval > 1) {
      const years = Math.floor(interval);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
    interval = seconds / 2592000;
    if (interval > 1) {
      const months = Math.floor(interval);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    }
    interval = seconds / 86400;
    if (interval > 1) {
      const days = Math.floor(interval);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    interval = seconds / 3600;
    if (interval > 1) {
      const hours = Math.floor(interval);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    interval = seconds / 60;
    if (interval > 1) {
      const minutes = Math.floor(interval);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    return 'just now';
  };

  const handleRestrictedAccess = () => {
    toast.error(
      '⚠️ You are restricted to access this, please contact Admin Team', {
      position: "bottom-left",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      closeButton: false,
      icon: false,
      style: {
        backgroundColor: "#FF474C",
        color: "black",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px",
        fontSize: "14px",
      },
    });
  };

  const handleNavigation = useCallback((path) => {
    scrollToTop();
    navigate(path);
  }, [navigate]);

  const features = [
    {
      id: 'support',
      icon: darkMode ? supportManagementIconDark : supportManagementIconLight,
      title: 'Support Management',
      description: 'Streamline ticket creation, tracking, and SLA monitoring — all in one place.',
      onClick: () => handleNavigation('/support-dashboard')
    },
    {
      id: 'monitoring',
      icon: darkMode ? monitoringIconDark : monitoringIconLight,
      title: 'Monitoring',
      description: 'Track cloud usage, infrastructure health, and GitHub activity in real-time. Get actionable insights and analytics — all in one view.',
      onClick: isAdmin || allowedSections.includes(featureSectionMapping['Monitoring']) ? () => handleNavigation('/monitoring-dashboard') : handleRestrictedAccess
    },
    {
      id: 'cloud',
      icon: darkMode ? cloudIconDark : cloudIconLight,
      title: 'Cloud',
      description: 'Manage cloud resources with confidence. Monitor usage, track costs, and optimize infrastructure performance — all in one place.',
      onClick: isAdmin || allowedSections.includes(featureSectionMapping['Cloud']) ? () => handleNavigation('/cloud') : handleRestrictedAccess
    },
    {
      id: 'service_config',
      icon: darkMode ? serviceConfigurationIconDark : serviceConfigurationIconLight,
      title: 'Service Configuration',
      description: 'Configure and monitor service performance with ease. Track settings, validate changes, and plan migrations confidently.',
      onClick: isAdmin || allowedSections.includes(featureSectionMapping['Service configuration']) ? () => handleNavigation('/database') : handleRestrictedAccess
    },
    {
      id: 'security',
      icon: darkMode ? securityIconDark : securityIconLight,
      title: 'Security',
      description: 'Safeguard your infrastructure with role-based access, threat monitoring, and audit logs — all in one secure space.',
      onClick: isAdmin || allowedSections.includes(featureSectionMapping['Security']) ? () => handleNavigation('/security-dashboard') : handleRestrictedAccess
    },
    {
      id: 'cicd',
      icon: darkMode ? cicdIconDark : cicdIconLight,
      title: 'CI/CD Flows',
      description: 'Visualize, manage, and trigger your build-test-deploy pipelines. Track deployment status and streamline releases with ease.',
      onClick: isAdmin || allowedSections.includes(featureSectionMapping['CI/CD Flows']) ? () => handleNavigation('/development') : handleRestrictedAccess
    },
    {
      id: 'qa',
      icon: darkMode ? qaTestingIconDark : qaTestingIconLight,
      title: 'QA & Testing',
      description: 'Monitor test runs across environments, track pass/fail rates, and analyze failures — all in one unified view.',
      onClick: isAdmin || allowedSections.includes(featureSectionMapping['QA & Testing']) ? () => handleNavigation('/qa-dashboard') : handleRestrictedAccess
    },
    {
      id: 'user_management',
      icon: darkMode ? userConfigIconDark : userConfigIconLight,
      title: 'User Configuration',
      description: 'Manage user access and permissions across DevOps Buddy. Easily grant or revoke section-level access using email or GitHub ID.',
      onClick: isAdmin ? () => handleNavigation('/user-configuration') : handleRestrictedAccess
    },
    {
      id: 'deep_dive',
      icon: darkMode ? platformDeepDiveIconDark : platformDeepDiveIconLight,
      title: 'Platform Deep Dive',
      description: 'Explore how to navigate key modules, trigger workflows, and monitor your infrastructure in real-time. Designed for new users to onboard faster and work smarter.',
      onClick: () => handleNavigation('/platform-deep-dive'),
      special: true
    }
  ];

  return (
    <PageContainer>
      <Grid container spacing={0} sx={{ '& > .MuiGrid-item': { padding: '12px' } }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ maxWidth: '470px', mb: 2 }}>
            <WelcomeTitle>👋 {getTimeBasedGreeting()}, {userName}!</WelcomeTitle>
            <Typography sx={{ fontSize: '0.875rem', color: '#64748B' }}>
              Here's a quick look at your operations today.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ maxWidth: '470px', marginLeft: 'auto', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: 2 }}>
            <WelcomeSubtitle sx={{ textAlign: 'right' }}>
              You have{' '}
              <a
                onClick={() => handleNavigation('/track-tickets?filter=open')}
                style={{
                  color: '#1674B4',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {openTicketsCount} open ticket{openTicketsCount !== 1 ? 's' : ''}
              </a>
            </WelcomeSubtitle>
            <WelcomeSubtitle sx={{ textAlign: 'right', fontWeight: 'bold', fontFamily: '"Proxima Nova"' }}>
              Last login: {lastLogin}
            </WelcomeSubtitle>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ mt: -2, mb: -10, borderColor: '#B1B1B1' }} />
        </Grid>
        {features.map((feature, index) => (
          <Grid item xs={12} md={feature.special ? 12 : 6} key={feature.id} sx={{ 
            padding: '12px !important',
            width: feature.special ? '100%' : 'calc(50% - 24px)',
            flex: feature.special ? '0 0 100%' : '0 0 auto',
            maxWidth: feature.special ? '100%' : 'calc(50% - 24px)'
          }}>
            <FeatureCard 
              elevation={0}
              onClick={feature.onClick}
              special={feature.special}
              sx={{
                cursor: feature.onClick ? 'pointer' : 'default',
                margin: '0 !important',
                width: '100%',
                maxWidth: feature.special ? '100%' : '470px',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <IconImage src={feature.icon} alt={`${feature.title} icon`} special={feature.special} />
              <CardContent>
                <Subtitle>{feature.title}</Subtitle>
                <CardDescription>
                  {feature.description}
                  {feature.id === 'deep_dive' && (
                    <>
                      {' '}
                      <ReadMoreLink>Read More</ReadMoreLink>
                    </>
                  )}
                </CardDescription>
                {feature.id === 'support' && (
                  <Box sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    mt: 1,
                    pt: 0.5,
                    pb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    pl: 1,
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <Box
                        component="a"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigation('/track-tickets?filter=open');
                        }}
                        sx={{
                          color: '#1674B4',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          marginRight: '4px',
                        }}
                      >
                        {openTicketsCount}
                      </Box>
                      <Box component="span" sx={{ color: '#64748B' }}>
                        open ticket{openTicketsCount !== 1 ? 's' : ''}
                      </Box>
                    </Typography>
                  </Box>
                )}

              </CardContent>
            </FeatureCard>
          </Grid>
        ))}
      </Grid>
    </PageContainer>
  );
}

export default DevOpsDashboard;
